/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import Component from 'can/component/component';
import template from './pie.stache';
import can from 'can';
import DomEventsManager from 'scripts/dom-events-manager';
import domUtils from 'scripts/utils/dom';
import { selectAll } from 'd3-selection';
import { interpolateNumber } from 'd3-interpolate';
import { arc as d3arc } from 'd3-shape';
import { easeCircleOut } from 'd3-ease';
import 'd3-transition';

const RADIUS_INNER = 112 / 2;
const RADIUS_OUTER = RADIUS_INNER + 1;
const ANGLE_START = Math.PI;
const getEndAngle = (percent) =>
    ANGLE_START + ((percent / 100) * Math.PI * 3) / 2;
const getArc = d3arc()
    .innerRadius(RADIUS_INNER)
    .outerRadius(RADIUS_OUTER)
    .startAngle(ANGLE_START);
const defaultArcPath = getArc({ endAngle: getEndAngle(100) });

Component.extend({
    tag: 'leaderboard-pie',
    template: template,
    leakScope: false,
    viewModel: {
        define: {
            round: {
                type: 'number',
                value: 1,
            },
            player: {
                Type: can.Map,
            },
            kpi: {
                type: 'string',
            },
            isFirstShow: {
                type: 'boolean',
                value: true,
            },
            stats: {
                Type: can.Map,
            },
            showPlace: {
                type: 'boolean',
                value: true,
            },
        },
    },
    events: {
        inserted($element) {
            this.element = $element;

            this.viewModel.bind('round', (ev, values) => {
                this.calcStat();
            });
            this.calcStat();

            DomEventsManager.subscribe(this);
        },

        calcStat() {
            let roundInfo = this.viewModel.attr('round')
                ? this.viewModel.attr(
                      `player.roundsInfo.${this.viewModel.attr('round') -
                          1}.stats`,
                  )
                : this.viewModel.attr('player.overallStats');

            roundInfo = roundInfo
                ? roundInfo
                      .filter(
                          (stat) => stat.name === this.viewModel.attr('kpi'),
                      )
                      .pop() || new can.Map({})
                : new can.Map({});

            if (!_.isEqual(roundInfo, this.previousRoundInfo)) {
                this.previousRoundInfo = roundInfo;
                this.viewModel.attr('stats', {
                    stat: roundInfo.attr('stat'),
                    statRank: roundInfo.attr('rank') || 0,
                    playerGraph: roundInfo.attr('stat'),
                    fieldGraph: roundInfo.attr('average'),
                    playerDefaultCoordinates: defaultArcPath,
                    fieldDefaultCoordinates: defaultArcPath,
                    fairwaysHit: roundInfo.attr('count') || 0,
                    holesPlayed: roundInfo.attr('total') || 0,
                });
                this.renderPieChart();
            }
        },

        removed() {
            DomEventsManager.unsubscribe(this);
        },

        animatePieChart(svg, percent = 0) {
            const path = selectAll(svg);
            const int = interpolateNumber(ANGLE_START, getEndAngle(percent));

            path.transition()
                .delay(500)
                .duration(1500)
                .attrTween('d', () => (progress) =>
                    getArc({ endAngle: int(easeCircleOut(progress)) }),
                );
        },

        onFirstShow() {
            if (
                this.element &&
                domUtils.isOnScreen(this.element, true) &&
                this.viewModel.attr('isFirstShow')
            ) {
                this.renderPieChart();
            }
        },

        renderPieChart() {
            const stats = this.viewModel.attr('stats');
            this.viewModel.attr('isFirstShow', false);
            const playerValue = stats.playerGraph
                ? _.isNil(stats.playerGraph.percentage)
                    ? stats.playerGraph.value
                    : stats.playerGraph.percentage
                : 0;
            const fieldGraph = stats.fieldGraph
                ? _.isNil(stats.fieldGraph.percentage)
                    ? stats.fieldGraph.value
                    : stats.fieldGraph.percentage
                : 0;

            this.animatePieChart(
                this.element.find('.js-svg-player-chart'),
                Math.round(playerValue * 100),
            );
            this.animatePieChart(
                this.element.find('.js-svg-field-chart'),
                Math.round(fieldGraph * 100),
            );
            DomEventsManager.unsubscribe(this);
        },
    },
});
